import { supabase, isSupabaseConfigured } from "../supabase";
import { transactionsApi } from "./transactions";
import { hoursApi } from "./hours";
import { notificationsApi } from "./notifications";

/**
 * Lessons API helpers using Supabase
 */

export interface GroupLesson {
  id: number;
  title: string;
  start_at: string;
  end_at: string;
  dance_style: string;
  instructor_id: number;
  max_participants: number;
  current_participants: number;
  /** Počet prihlásených (confirmed/checked_in) podľa users.gender */
  participants_female?: number;
  participants_male?: number;
  status: string;
  location?: string | null;
  duration_hours?: number | null;
  created_at: string;
  group_id?: number | null;
  price_per_person_eur?: number | null;
}

export interface PrivateLesson {
  id: number;
  user_id: string; // UUID
  start_at: string;
  end_at: string;
  duration_hours: number;
  lesson_type: "single" | "pair";
  pair_id?: number;
  instructor_id?: string; // UUID
  availability_slot_id?: number | null;
  status: string;
  notes?: string;
  created_at: string;
}

export interface GroupSignup {
  id: number;
  user_id: string; // UUID
  lesson_id: number;
  pair_id?: number;
  status: string;
  created_at: string;
}

async function createPrivateLessonForSlotFallback(payload: {
  availability_slot_id: number;
  user_id: string;
  lesson_type: "single" | "pair";
  pair_id?: number | null;
  notes?: string | null;
  status: "pending" | "approved";
  instructor_id?: string | null;
}): Promise<{ data: PrivateLesson | null; error: unknown }> {
  const { data: slot, error: slotError } = await supabase
    .from("availability_slots")
    .select("*")
    .eq("id", payload.availability_slot_id)
    .single();

  if (slotError) {
    return { data: null, error: slotError };
  }

  if (!slot) {
    return { data: null, error: new Error("Availability slot not found") };
  }

  if (slot.status === "blocked") {
    return { data: null, error: new Error("Availability slot is blocked") };
  }

  if (slot.status === "pending" && slot.requested_by !== payload.user_id) {
    return {
      data: null,
      error: new Error("Pending availability slot belongs to another request"),
    };
  }

  const { data: existing } = await supabase
    .from("private_lessons")
    .select("id")
    .eq("availability_slot_id", payload.availability_slot_id)
    .in("status", ["pending", "approved", "confirmed", "completed"])
    .maybeSingle();

  if (existing) {
    return {
      data: null,
      error: { code: "23505", message: "Availability slot is already booked" },
    };
  }

  const instructorId = payload.instructor_id ?? slot.instructor_id ?? null;
  const startAt = String(slot.start_at);
  const durationHours = Number(slot.duration_hours);
  const endAt = new Date(
    new Date(startAt).getTime() + durationHours * 60 * 60 * 1000,
  ).toISOString();

  const lessonPayload: Record<string, string | number | null> = {
    user_id: payload.user_id,
    start_at: startAt,
    end_at: endAt,
    duration_hours: durationHours,
    lesson_type: payload.lesson_type,
    pair_id: payload.pair_id ?? null,
    instructor_id: instructorId,
    status: payload.status,
    notes: payload.notes ?? null,
    availability_slot_id: payload.availability_slot_id,
  };

  let insertResult = await supabase
    .from("private_lessons")
    .insert(lessonPayload)
    .select()
    .single();

  if ((insertResult.error as { code?: string } | null)?.code === "PGRST204") {
    // Production schema may still be missing the availability_slot_id migration.
    // Keep the booking flow usable and mark the slot booked even without the explicit link.
    delete lessonPayload.availability_slot_id;
    insertResult = await supabase
      .from("private_lessons")
      .insert(lessonPayload)
      .select()
      .single();
  }

  if (insertResult.error) {
    return { data: null, error: insertResult.error };
  }

  const { error: updateError } = await supabase
    .from("availability_slots")
    .update({
      status: "booked",
      instructor_id: instructorId,
      requested_by: null,
      request_notes: null,
      updated_at: new Date().toISOString(),
    })
    .eq("id", payload.availability_slot_id);

  if (updateError) {
    return { data: null, error: updateError };
  }

  return { data: insertResult.data as PrivateLesson, error: null };
}

export const lessonsApi = {
  /**
   * Fetch group lessons with filters
   */
  async list(filters?: Record<string, unknown>): Promise<{
    success: boolean;
    data?: GroupLesson[];
    error?: string;
  }> {
    try {
      let query = supabase.from("group_lessons").select("*");

      // Apply filters
      if (filters?.upcoming) {
        query = query.gte("start_at", new Date().toISOString());
      }
      if (filters?.status) {
        query = query.eq("status", filters.status);
      }
      if (filters?.dance_style) {
        query = query.eq("dance_style", filters.dance_style);
      }

      query = query.order("start_at", { ascending: true });

      const { data, error } = await query;

      if (error) throw error;

      return { success: true, data: data as GroupLesson[] };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Failed to fetch lessons",
      };
    }
  },

  /**
   * Book a group lesson
   */
  async book(
    lessonId: number,
    pairId?: number,
  ): Promise<{ success: boolean; data?: GroupSignup; error?: string }> {
    try {
      // Get current user from auth
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      const signupData: {
        user_id: string;
        lesson_id: number;
        pair_id?: number;
        status: string;
      } = {
        user_id: user.id,
        lesson_id: lessonId,
        status: "pending",
      };

      if (pairId) {
        signupData.pair_id = pairId;
      }

      const { data, error } = await supabase
        .from("group_signups")
        .insert(signupData)
        .select()
        .single();

      if (error) throw error;

      return { success: true, data: data as GroupSignup };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to book lesson",
      };
    }
  },

  /**
   * Používateľ si rezervuje súkromnú lekciu. Vytvorí sa so statusom Čaká (pending).
   */
  async bookPrivate(payload: {
    user_id: string;
    start_at: string;
    duration_hours: number;
    lesson_type: "single" | "pair";
    pair_id?: number;
    availability_slot_id?: number;
    notes?: string;
  }): Promise<{ success: boolean; data?: PrivateLesson; error?: string }> {
    try {
      const endAt = new Date(
        new Date(payload.start_at).getTime() +
          payload.duration_hours * 60 * 60 * 1000,
      ).toISOString();

      let data: unknown;
      let error: unknown;

      if (payload.availability_slot_id) {
        const rpcResult = await supabase.rpc("book_private_lesson_for_slot", {
          p_availability_slot_id: payload.availability_slot_id,
          p_user_id: payload.user_id,
          p_lesson_type: payload.lesson_type,
          p_pair_id: payload.pair_id ?? null,
          p_notes: payload.notes ?? null,
          p_status: "pending",
        });
        data = rpcResult.data;
        error = rpcResult.error;

        if ((error as { code?: string } | null)?.code === "PGRST202") {
          const fallbackResult = await createPrivateLessonForSlotFallback({
            availability_slot_id: payload.availability_slot_id,
            user_id: payload.user_id,
            lesson_type: payload.lesson_type,
            pair_id: payload.pair_id ?? null,
            notes: payload.notes ?? null,
            status: "pending",
          });
          data = fallbackResult.data;
          error = fallbackResult.error;
        }
      } else {
        const lessonData = {
          user_id: payload.user_id,
          start_at: payload.start_at,
          end_at: endAt,
          duration_hours: payload.duration_hours,
          lesson_type: payload.lesson_type,
          pair_id: payload.pair_id,
          status: "pending",
          notes: payload.notes,
          availability_slot_id: null,
        };

        const insertResult = await supabase
          .from("private_lessons")
          .insert(lessonData)
          .select()
          .single();
        data = insertResult.data;
        error = insertResult.error;
      }

      if (error) throw error;

      return { success: true, data: data as PrivateLesson };
    } catch (error) {
      const databaseError = error as { code?: string; message?: string };

      if (databaseError.code === "23505") {
        return {
          success: false,
          error:
            "Tento termín už bol práve rezervovaný. Vyberte si prosím iný voľný slot.",
        };
      }

      if (databaseError.code === "42501") {
        return {
          success: false,
          error: "Nemáte oprávnenie rezervovať tento termín.",
        };
      }

      return {
        success: false,
        error:
          databaseError.message ||
          (error instanceof Error
            ? error.message
            : "Failed to request private lesson"),
      };
    }
  },

  /**
   * Admin vytvorí súkromnú lekciu – ihneď so statusom Schválené (approved).
   * (Používateľská rezervácia je vždy len Čaká, cez bookPrivate.)
   */
  async createPrivateAsAdmin(payload: {
    user_id: string;
    start_at: string;
    duration_hours: number;
    lesson_type: "single" | "pair";
    pair_id?: number;
    instructor_id?: string;
    availability_slot_id?: number;
    notes?: string;
    status?: "pending" | "approved";
  }): Promise<{ success: boolean; data?: PrivateLesson; error?: string }> {
    try {
      const endAt = new Date(
        new Date(payload.start_at).getTime() +
          payload.duration_hours * 60 * 60 * 1000,
      ).toISOString();

      const status = payload.status ?? "approved";

      let data: unknown;
      let error: unknown;

      if (payload.availability_slot_id) {
        const rpcResult = await supabase.rpc("book_private_lesson_for_slot", {
          p_availability_slot_id: payload.availability_slot_id,
          p_user_id: payload.user_id,
          p_lesson_type: payload.lesson_type,
          p_pair_id: payload.pair_id ?? null,
          p_notes: payload.notes ?? null,
          p_status: status,
        });
        data = rpcResult.data;
        error = rpcResult.error;

        if ((error as { code?: string } | null)?.code === "PGRST202") {
          const fallbackResult = await createPrivateLessonForSlotFallback({
            availability_slot_id: payload.availability_slot_id,
            user_id: payload.user_id,
            lesson_type: payload.lesson_type,
            pair_id: payload.pair_id ?? null,
            notes: payload.notes ?? null,
            status,
            instructor_id: payload.instructor_id ?? null,
          });
          data = fallbackResult.data;
          error = fallbackResult.error;
        }
      } else {
        const lessonData = {
          user_id: payload.user_id,
          start_at: payload.start_at,
          end_at: endAt,
          duration_hours: payload.duration_hours,
          lesson_type: payload.lesson_type,
          pair_id: payload.pair_id ?? null,
          instructor_id: payload.instructor_id ?? null,
          status,
          notes: payload.notes ?? null,
          availability_slot_id: null,
        };

        const insertResult = await supabase
          .from("private_lessons")
          .insert(lessonData)
          .select()
          .single();
        data = insertResult.data;
        error = insertResult.error;
      }

      if (error) throw error;

      // Hodiny sa neodpočítavajú pri vytvorení ani pri approved – až pri Dokončené (completed)
      return { success: true, data: data as PrivateLesson };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : "Nepodarilo sa vytvoriť lekciu",
      };
    }
  },

  /**
   * Fetch private lessons (for admin) with pagination
   * Includes user information via join
   * @param filters - status, upcoming, lesson_type
   * @param limit - max počet lekcií (default 20)
   * @param offset - offset pre stránkovanie (default 0)
   */
  async listAllPrivate(
    filters?: Record<string, unknown> & { limit?: number; offset?: number },
  ): Promise<{
    success: boolean;
    data?: (PrivateLesson & {
      user_email?: string;
      user_display_name?: string;
      user_avatar_url?: string | null;
    })[];
    hasMore?: boolean;
    error?: string;
  }> {
    try {
      const usePagination =
        typeof filters?.limit === "number" ||
        typeof filters?.offset === "number";
      const limit = filters?.limit ?? 20;
      const offset = filters?.offset ?? 0;

      let query = supabase.from("private_lessons").select("*");

      // Apply filters
      if (filters?.status) {
        query = query.eq("status", filters.status);
      }
      if (filters?.upcoming) {
        query = query.gte("start_at", new Date().toISOString());
      }
      if (filters?.lesson_type) {
        query = query.eq("lesson_type", filters.lesson_type);
      }

      query = query.order("start_at", { ascending: false });

      if (usePagination) {
        // Načítať limit+1 pre zistenie, či sú ďalšie záznamy
        query = query.range(offset, offset + limit);
      }

      const { data: rawLessons, error } = await query;

      if (error) throw error;

      const hasMore = usePagination && (rawLessons?.length ?? 0) > limit;
      const lessons = usePagination
        ? (rawLessons ?? []).slice(0, limit)
        : (rawLessons ?? []);

      if (lessons.length === 0) {
        return { success: true, data: [], hasMore: false };
      }

      // Get unique user IDs
      type LessonWithUserId = PrivateLesson & { user_id: string };
      const userIds = [
        ...new Set(
          (lessons as LessonWithUserId[]).map((l) => l.user_id).filter(Boolean),
        ),
      ];

      // Fetch user information
      interface UserInfo {
        id: string;
        email: string;
        display_name: string | null;
        avatar_url: string | null;
      }

      const { data: users, error: usersError } = await supabase
        .from("users")
        .select("id, email, display_name, avatar_url")
        .in("id", userIds);

      if (usersError) throw usersError;

      // Create a map for quick lookup
      const userMap = new Map<string, UserInfo>(
        ((users as UserInfo[]) || []).map((u) => [u.id, u]),
      );

      // Transform data to include user info
      const transformedData = (lessons as LessonWithUserId[]).map((lesson) => {
        const userInfo = userMap.get(lesson.user_id);
        return {
          ...lesson,
          user_email: userInfo?.email || null,
          user_display_name: userInfo?.display_name || null,
          user_avatar_url: userInfo?.avatar_url || null,
        };
      });

      return {
        success: true,
        data: transformedData as (PrivateLesson & {
          user_email?: string;
          user_display_name?: string;
          user_avatar_url?: string | null;
        })[],
        hasMore,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : "Failed to fetch private lessons",
      };
    }
  },

  /**
   * Fetch aggregate statistics for private lessons (admin overview)
   */
  async getPrivateLessonsStats(): Promise<{
    success: boolean;
    data?: {
      total: number;
      pending: number;
      approved: number;
      completed: number;
      cancelled: number;
      upcoming: number;
      upcomingSingle: number;
      upcomingPair: number;
      pendingSingle: number;
      pendingPair: number;
      revenueTotal: number;
      revenueUpcoming: number;
      revenuePending: number;
    };
    error?: string;
  }> {
    try {
      if (!isSupabaseConfigured) {
        return {
          success: true,
          data: {
            total: 0,
            pending: 0,
            approved: 0,
            completed: 0,
            cancelled: 0,
            upcoming: 0,
            upcomingSingle: 0,
            upcomingPair: 0,
            pendingSingle: 0,
            pendingPair: 0,
            revenueTotal: 0,
            revenueUpcoming: 0,
            revenuePending: 0,
          },
        };
      }

      const now = new Date().toISOString();
      const rates = { single: 47, pair: 60 }; // €/h - match reports/revenue-calculations

      const [
        totalRes,
        pendingRes,
        approvedRes,
        completedRes,
        cancelledRes,
        upcomingRes,
        completedLessonsRes,
        upcomingLessonsRes,
        pendingLessonsRes,
      ] = await Promise.all([
        supabase
          .from("private_lessons")
          .select("id", { count: "exact", head: true }),
        supabase
          .from("private_lessons")
          .select("id", { count: "exact", head: true })
          .eq("status", "pending"),
        supabase
          .from("private_lessons")
          .select("id", { count: "exact", head: true })
          .eq("status", "approved"),
        supabase
          .from("private_lessons")
          .select("id", { count: "exact", head: true })
          .eq("status", "completed"),
        supabase
          .from("private_lessons")
          .select("id", { count: "exact", head: true })
          .eq("status", "cancelled"),
        supabase
          .from("private_lessons")
          .select("id", { count: "exact", head: true })
          .in("status", ["pending", "approved"])
          .gte("start_at", now),
        supabase
          .from("private_lessons")
          .select("duration_hours, lesson_type")
          .eq("status", "completed"),
        supabase
          .from("private_lessons")
          .select("duration_hours, lesson_type")
          .in("status", ["pending", "approved"])
          .gte("start_at", now),
        supabase
          .from("private_lessons")
          .select("duration_hours, lesson_type")
          .eq("status", "pending"),
      ]);

      let revenueTotal = 0;
      if (completedLessonsRes.data) {
        for (const lesson of completedLessonsRes.data) {
          const duration = Number(lesson.duration_hours || 0);
          const rate =
            lesson.lesson_type === "pair" ? rates.pair : rates.single;
          revenueTotal += duration * rate;
        }
      }

      let revenuePending = 0;
      let pendingSingle = 0;
      let pendingPair = 0;
      if (pendingLessonsRes.data) {
        for (const lesson of pendingLessonsRes.data) {
          const duration = Number(lesson.duration_hours || 0);
          const rate =
            lesson.lesson_type === "pair" ? rates.pair : rates.single;
          revenuePending += duration * rate;
          if (lesson.lesson_type === "pair") {
            pendingPair += 1;
          } else {
            pendingSingle += 1;
          }
        }
      }

      let revenueUpcoming = 0;
      let upcomingSingle = 0;
      let upcomingPair = 0;
      if (upcomingLessonsRes.data) {
        for (const lesson of upcomingLessonsRes.data) {
          const duration = Number(lesson.duration_hours || 0);
          const rate =
            lesson.lesson_type === "pair" ? rates.pair : rates.single;
          revenueUpcoming += duration * rate;
          if (lesson.lesson_type === "pair") {
            upcomingPair += 1;
          } else {
            upcomingSingle += 1;
          }
        }
      }

      return {
        success: true,
        data: {
          total: totalRes.count ?? 0,
          pending: pendingRes.count ?? 0,
          approved: approvedRes.count ?? 0,
          completed: completedRes.count ?? 0,
          cancelled: cancelledRes.count ?? 0,
          upcoming: upcomingRes.count ?? 0,
          upcomingSingle,
          upcomingPair,
          pendingSingle,
          pendingPair,
          revenueTotal,
          revenueUpcoming,
          revenuePending,
        },
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : "Failed to fetch private lesson statistics",
      };
    }
  },

  /**
   * Helper: odpočíta hodiny za súkromnú lekciu (volané pri prechode na status completed).
   */
  async deductHoursForPrivateLesson(lesson: PrivateLesson): Promise<{
    success: boolean;
    transactionId?: number;
    error?: string;
  }> {
    try {
      // Determine package type based on lesson type
      const packageType =
        lesson.lesson_type === "single" ? "private_single" : "private_pair";
      const durationHours = Number(lesson.duration_hours);

      // Get current user hours
      const hoursResult = await hoursApi.getUserHours(
        lesson.user_id,
        packageType,
      );

      if (!hoursResult.success) {
        return {
          success: false,
          error: hoursResult.error || "Failed to fetch user hours",
        };
      }

      // Check if user has hours record
      const hoursData = Array.isArray(hoursResult.data)
        ? hoursResult.data[0]
        : hoursResult.data;

      if (!hoursData) {
        return {
          success: false,
          error: `User does not have hours for package type: ${packageType}`,
        };
      }

      const currentRemaining = Number(hoursData.hours_remaining);
      if (currentRemaining < durationHours) {
        return {
          success: false,
          error: `Insufficient hours. Required: ${durationHours}, Available: ${currentRemaining}`,
        };
      }

      // Calculate new balance
      const newRemaining = currentRemaining - durationHours;
      const newUsed = Number(hoursData.hours_used) + durationHours;

      // Create transaction of type "use" (metadata obsahuje slot/termín pre zobrazenie v histórii)
      const transactionResult = await transactionsApi.create({
        user_id: lesson.user_id,
        transaction_type: "use",
        hours_delta: -durationHours, // Negative because we're deducting
        amount: 0,
        metadata: {
          private_lesson_id: lesson.id,
          lesson_type: lesson.lesson_type,
          duration_hours: durationHours,
          start_at: lesson.start_at, // termín odpočítaného slotu pre "Moje transakcie"
        },
        balance_after: newRemaining,
      });

      if (!transactionResult.success || !transactionResult.data) {
        return {
          success: false,
          error: transactionResult.error || "Failed to create transaction",
        };
      }

      const transaction = transactionResult.data;

      // Update user_hours
      const updateResult = await hoursApi.updateUserHours(
        lesson.user_id,
        packageType,
        {
          hours_remaining: newRemaining,
          hours_used: newUsed,
          last_transaction_id: transaction.id,
        },
      );

      if (!updateResult.success) {
        console.error(
          "Failed to update user hours after transaction:",
          updateResult.error,
        );
        // Continue anyway - transaction was created
      }

      return { success: true, transactionId: transaction.id };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : "Failed to deduct hours for private lesson",
      };
    }
  },

  /**
   * Approve private lesson (change status to 'approved')
   * Also deducts hours from user_hours and creates a transaction
   * @deprecated Use approvePrivateLessonAsApproved instead
   */
  async approvePrivateLesson(lessonId: number): Promise<{
    success: boolean;
    data?: PrivateLesson;
    error?: string;
  }> {
    // Redirect to approvePrivateLessonAsApproved for consistency
    return this.approvePrivateLessonAsApproved(lessonId);
  },

  /**
   * Approve private lesson without deducting hours from package.
   * Use when user pays cash or will buy package later.
   */
  async approvePrivateLessonWithoutDeduction(lessonId: number): Promise<{
    success: boolean;
    data?: PrivateLesson;
    error?: string;
  }> {
    try {
      const { data: lesson, error: lessonError } = await supabase
        .from("private_lessons")
        .select("*")
        .eq("id", lessonId)
        .single();

      if (lessonError) throw lessonError;
      if (!lesson) throw new Error("Lesson not found");

      if (lesson.status === "approved") {
        return { success: true, data: lesson as PrivateLesson };
      }

      const { data, error } = await supabase
        .from("private_lessons")
        .update({ status: "approved" })
        .eq("id", lessonId)
        .select()
        .single();

      if (error) throw error;

      return { success: true, data: data as PrivateLesson };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : "Failed to approve private lesson",
      };
    }
  },

  /**
   * Approve private lesson (change status to 'approved').
   * Hodiny sa neodpočítavajú – odpočítajú sa až pri Dokončené (completed).
   */
  async approvePrivateLessonAsApproved(lessonId: number): Promise<{
    success: boolean;
    data?: PrivateLesson;
    error?: string;
  }> {
    try {
      const { data: lesson, error: lessonError } = await supabase
        .from("private_lessons")
        .select("*")
        .eq("id", lessonId)
        .single();

      if (lessonError) throw lessonError;
      if (!lesson) throw new Error("Lesson not found");

      if (lesson.status === "approved") {
        return { success: true, data: lesson as PrivateLesson };
      }

      const { data, error } = await supabase
        .from("private_lessons")
        .update({ status: "approved" })
        .eq("id", lessonId)
        .select()
        .single();

      if (error) throw error;

      return { success: true, data: data as PrivateLesson };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : "Failed to approve private lesson",
      };
    }
  },

  /**
   * Cancel private lesson (change status to 'cancelled').
   * Refund hodín len ak bola lekcia Dokončená (completed) – teda hodiny boli už odpočítané.
   */
  async cancelPrivateLesson(lessonId: number): Promise<{
    success: boolean;
    data?: PrivateLesson;
    error?: string;
  }> {
    try {
      const { data: lesson, error: lessonError } = await supabase
        .from("private_lessons")
        .select("*")
        .eq("id", lessonId)
        .single();

      if (lessonError) throw lessonError;
      if (!lesson) throw new Error("Lesson not found");

      // Refund len ak boli hodiny už odpočítané (lekcia bola completed)
      const wasCompleted = lesson.status === "completed";

      if (wasCompleted) {
        // Determine package type based on lesson type
        const packageType =
          lesson.lesson_type === "single" ? "private_single" : "private_pair";
        const durationHours = Number(lesson.duration_hours);

        // Get current user hours
        const hoursResult = await hoursApi.getUserHours(
          lesson.user_id,
          packageType,
        );

        if (hoursResult.success && hoursResult.data) {
          const hoursData = Array.isArray(hoursResult.data)
            ? hoursResult.data[0]
            : hoursResult.data;

          if (hoursData) {
            // Calculate new balance (refund hours)
            const newRemaining =
              Number(hoursData.hours_remaining) + durationHours;
            const newUsed = Math.max(
              0,
              Number(hoursData.hours_used) - durationHours,
            );

            // Create transaction of type "refund" (start_at pre zobrazenie v histórii)
            const transactionResult = await transactionsApi.create({
              user_id: lesson.user_id,
              transaction_type: "refund",
              hours_delta: durationHours, // Positive because we're refunding
              amount: 0,
              metadata: {
                private_lesson_id: lessonId,
                lesson_type: lesson.lesson_type,
                duration_hours: durationHours,
                cancelled: true,
                start_at: lesson.start_at,
              },
              balance_after: newRemaining,
            });

            if (transactionResult.success && transactionResult.data) {
              const transaction = transactionResult.data;

              // Update user_hours
              await hoursApi.updateUserHours(lesson.user_id, packageType, {
                hours_remaining: newRemaining,
                hours_used: newUsed,
                last_transaction_id: transaction.id,
              });
            }
          }
        }
      }

      // Update lesson status to 'cancelled'
      const { data, error } = await supabase
        .from("private_lessons")
        .update({ status: "cancelled" })
        .eq("id", lessonId)
        .select()
        .single();

      if (error) throw error;

      return { success: true, data: data as PrivateLesson };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : "Failed to cancel private lesson",
      };
    }
  },

  /**
   * Dokončiť lekciu s evidovaním platby v hotovosti (používateľ nemá balík/hodiny).
   * Vytvorí transakciu "use" s hodinami a sumou pre účely evidencie a reportov.
   */
  async completeLessonWithCashPayment(
    lessonId: number,
    amount: number,
  ): Promise<{ success: boolean; data?: PrivateLesson; error?: string }> {
    try {
      const { data: lesson, error: lessonError } = await supabase
        .from("private_lessons")
        .select("*")
        .eq("id", lessonId)
        .single();

      if (lessonError) throw lessonError;
      if (!lesson) throw new Error("Lesson not found");
      if (lesson.status === "completed") {
        return { success: true, data: lesson as PrivateLesson };
      }

      const durationHours = Number(lesson.duration_hours);

      const transactionResult = await transactionsApi.create({
        user_id: lesson.user_id,
        transaction_type: "use",
        hours_delta: -durationHours,
        amount: Math.max(0, amount),
        metadata: {
          private_lesson_id: lesson.id,
          lesson_type: lesson.lesson_type,
          duration_hours: durationHours,
          start_at: lesson.start_at,
          cash_payment: true,
        },
      });

      if (!transactionResult.success) {
        return {
          success: false,
          error: transactionResult.error || "Nepodarilo sa vytvoriť transakciu",
        };
      }

      const { data, error } = await supabase
        .from("private_lessons")
        .update({ status: "completed" })
        .eq("id", lessonId)
        .select()
        .single();

      if (error) throw error;
      return { success: true, data: data as PrivateLesson };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : "Failed to complete lesson with cash payment",
      };
    }
  },

  /**
   * Update private lesson status.
   * Approve = len zmena na approved (bez odpočítania). Completed = odpočítanie hodín. Cancel = refund len ak bola completed.
   * @param options.skipDeduction - pri "completed" len zmení status, neodpočíta hodiny (admin override)
   */
  async updatePrivateLessonStatus(
    lessonId: number,
    newStatus: string,
    options?: { skipDeduction?: boolean },
  ): Promise<{ success: boolean; data?: PrivateLesson; error?: string }> {
    if (newStatus === "approved") {
      return this.approvePrivateLessonAsApproved(lessonId);
    }
    if (newStatus === "cancelled") {
      return this.cancelPrivateLesson(lessonId);
    }
    if (newStatus === "completed") {
      try {
        const { data: lesson, error: lessonError } = await supabase
          .from("private_lessons")
          .select("*")
          .eq("id", lessonId)
          .single();

        if (lessonError) throw lessonError;
        if (!lesson) throw new Error("Lesson not found");

        // Hodiny sa odpočítavajú až pri prechode na Dokončené (iba raz), pokiaľ nie skipDeduction
        if (lesson.status !== "completed" && !options?.skipDeduction) {
          const deductResult = await this.deductHoursForPrivateLesson(
            lesson as PrivateLesson,
          );
          if (!deductResult.success) {
            return {
              success: false,
              error:
                deductResult.error ||
                "Nepodarilo sa odpočítať hodiny. Skontrolujte zostatok balíka.",
            };
          }
        }

        const { data, error } = await supabase
          .from("private_lessons")
          .update({ status: "completed" })
          .eq("id", lessonId)
          .select()
          .single();

        if (error) throw error;
        return { success: true, data: data as PrivateLesson };
      } catch (error) {
        return {
          success: false,
          error:
            error instanceof Error
              ? error.message
              : "Failed to update lesson status",
        };
      }
    }
    // Fallback: generic status update for other statuses (e.g. restore to pending)
    try {
      const { data, error } = await supabase
        .from("private_lessons")
        .update({ status: newStatus })
        .eq("id", lessonId)
        .select()
        .single();

      if (error) throw error;
      return { success: true, data: data as PrivateLesson };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : "Failed to update lesson status",
      };
    }
  },

  /**
   * Update private lesson term (start_at, duration_hours).
   * Recalculates end_at from start_at + duration_hours.
   */
  async updatePrivateLesson(
    lessonId: number,
    payload: { start_at?: string; duration_hours?: number },
  ): Promise<{ success: boolean; data?: PrivateLesson; error?: string }> {
    try {
      if (!payload.start_at && payload.duration_hours === undefined) {
        return { success: true, data: undefined };
      }

      const { data: lesson, error: lessonError } = await supabase
        .from("private_lessons")
        .select("start_at, duration_hours")
        .eq("id", lessonId)
        .single();

      if (lessonError) throw lessonError;
      if (!lesson) throw new Error("Lesson not found");

      const startAt = payload.start_at || lesson.start_at;
      const durationHours =
        payload.duration_hours !== undefined
          ? payload.duration_hours
          : Number(lesson.duration_hours);
      const endAt = new Date(
        new Date(startAt).getTime() + durationHours * 60 * 60 * 1000,
      ).toISOString();

      const updateData: {
        start_at?: string;
        end_at: string;
        duration_hours?: number;
      } = {
        end_at: endAt,
      };
      if (payload.start_at) updateData.start_at = payload.start_at;
      if (payload.duration_hours !== undefined) {
        updateData.duration_hours = payload.duration_hours;
      }

      const { data, error } = await supabase
        .from("private_lessons")
        .update(updateData)
        .eq("id", lessonId)
        .select()
        .single();

      if (error) throw error;
      return { success: true, data: data as PrivateLesson };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : "Failed to update private lesson",
      };
    }
  },

  /**
   * Fetch all group lessons (for admin)
   */
  async listAllGroup(filters?: Record<string, unknown>): Promise<{
    success: boolean;
    data?: GroupLesson[];
    error?: string;
  }> {
    try {
      let query = supabase
        .from("group_lessons")
        .select("*, dance_groups!group_lessons_group_id_fkey(id, name)");

      // Apply filters
      if (filters?.status) {
        query = query.eq("status", filters.status);
      }
      if (filters?.upcoming) {
        query = query.gte("start_at", new Date().toISOString());
      }
      if (filters?.dance_style) {
        query = query.eq("dance_style", filters.dance_style);
      }

      query = query.order("start_at", { ascending: false });

      const { data, error } = await query;

      if (error) throw error;

      const rows = (data ?? []) as Record<string, unknown>[];
      if (rows.length === 0) {
        return { success: true, data: [] };
      }

      const lessonIds = rows.map((row) => Number(row.id));
      const { data: countRows, error: rpcError } = await supabase.rpc(
        "group_lesson_participant_counts",
        { p_lesson_ids: lessonIds },
      );

      if (rpcError) {
        console.error("group_lesson_participant_counts:", rpcError);
      }

      interface ParticipantCountRow {
        lesson_id: number;
        female_count: number;
        male_count: number;
        total_count: number;
      }

      const countMap = new Map<
        number,
        { female: number; male: number; total: number }
      >();
      for (const raw of (countRows ?? []) as ParticipantCountRow[]) {
        const lid = Number(raw.lesson_id);
        countMap.set(lid, {
          female: Number(raw.female_count),
          male: Number(raw.male_count),
          total: Number(raw.total_count),
        });
      }

      const enriched: GroupLesson[] = rows.map((lesson) => {
        const id = Number(lesson.id);
        const c = countMap.get(id);
        const capacity = Number(lesson.capacity ?? 20);
        const danceGroup = lesson.dance_groups as
          | { id?: number; name?: string }
          | null
          | undefined;
        return {
          ...(lesson as unknown as GroupLesson),
          max_participants: capacity,
          current_participants: c?.total ?? 0,
          participants_female: c?.female ?? 0,
          participants_male: c?.male ?? 0,
          group_name: danceGroup?.name ?? null,
        };
      });

      return { success: true, data: enriched };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : "Failed to fetch group lessons",
      };
    }
  },

  /**
   * Create a new group lesson (for admin)
   */
  async createGroupLesson(payload: {
    title: string;
    start_at: string;
    end_at: string;
    dance_style: string;
    instructor_id: number;
    max_participants: number;
    location?: string;
    duration_hours?: number;
    status?: string;
    notes?: string;
    group_id?: number;
    price_per_person_eur?: number;
  }): Promise<{ success: boolean; data?: GroupLesson; error?: string }> {
    try {
      const lessonData: Record<string, unknown> = {
        title: payload.title,
        start_at: payload.start_at,
        end_at: payload.end_at,
        dance_style: payload.dance_style,
        instructor_id: payload.instructor_id,
        capacity: payload.max_participants, // Databáza používa 'capacity'
        min_pairs: Math.ceil(payload.max_participants / 2), // Min páry
        location: payload.location || null,
        duration_hours: payload.duration_hours || null,
        status: payload.status || "scheduled",
        notes: payload.notes || null,
      };

      // Pridať group_id len ak je poskytnuté (pre lekcie vytvorené skupinami)
      if (payload.group_id !== undefined) {
        lessonData.group_id = payload.group_id;
      }
      if (payload.price_per_person_eur !== undefined) {
        lessonData.price_per_person_eur = payload.price_per_person_eur;
      }

      const { data, error } = await supabase
        .from("group_lessons")
        .insert(lessonData)
        .select()
        .single();

      if (error) throw error;

      return { success: true, data: data as GroupLesson };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : "Failed to create group lesson",
      };
    }
  },

  /**
   * Schváliť skupinovú lekciu navrhnutú skupinou
   * Po schválení sa automaticky prihlásia všetci členovia skupiny
   */
  async approveGroupLesson(
    lessonId: number,
  ): Promise<{ success: boolean; data?: GroupLesson; error?: string }> {
    try {
      // Získať lekciu s informáciou o skupine
      const { data: lesson, error: lessonError } = await supabase
        .from("group_lessons")
        .select(
          "*, dance_groups!group_lessons_group_id_fkey(id, name, creator_id)",
        )
        .eq("id", lessonId)
        .single();

      if (lessonError) throw lessonError;
      if (!lesson) throw new Error("Lesson not found");

      // Aktualizovať status na "scheduled"
      const { data: updatedLesson, error: updateError } = await supabase
        .from("group_lessons")
        .update({ status: "scheduled", updated_at: new Date().toISOString() })
        .eq("id", lessonId)
        .select()
        .single();

      if (updateError) throw updateError;

      // Ak má lekcia group_id, automaticky prihlásiť všetkých členov skupiny
      if (lesson.group_id) {
        const { data: members } = await supabase
          .from("group_members")
          .select("user_id")
          .eq("group_id", lesson.group_id)
          .eq("status", "accepted");

        const userIds = members?.map((m) => m.user_id) || [];
        const group = lesson.dance_groups as {
          id: number;
          name: string;
          creator_id: string;
        } | null;

        // Pridať aj tvorcu skupiny, ak nie je medzi členmi
        if (group && !userIds.includes(group.creator_id)) {
          userIds.push(group.creator_id);
        }

        // Prihlásiť všetkých členov
        for (const userId of userIds) {
          try {
            await supabase.from("group_signups").insert({
              user_id: userId,
              lesson_id: lessonId,
              status: "confirmed",
            });
          } catch (err) {
            console.error(`Failed to signup user ${userId}:`, err);
            // Pokračovať aj keď jeden zlyhá
          }
        }

        // Vytvoriť notifikácie pre všetkých členov o schválení
        const lessonDate = new Date(lesson.start_at).toLocaleDateString(
          "sk-SK",
        );
        const lessonTime = new Date(lesson.start_at).toLocaleTimeString(
          "sk-SK",
          {
            hour: "2-digit",
            minute: "2-digit",
          },
        );

        for (const userId of userIds) {
          await notificationsApi.create({
            user_id: userId,
            type: "group_lesson_created",
            title: `Skupinová lekcia schválená: ${lesson.title}`,
            message: `Vaša skupinová lekcia "${lesson.title}" bola schválená! Dátum: ${lessonDate}, čas: ${lessonTime}.`,
            data: {
              lesson_id: lessonId,
              lesson_title: lesson.title,
              lesson_date: lesson.start_at,
              group_id: lesson.group_id,
              group_name: group?.name || "",
            },
            sendEmail: true,
          });
        }
      }

      return { success: true, data: updatedLesson as GroupLesson };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : "Failed to approve group lesson",
      };
    }
  },

  /**
   * Zamietnuť skupinovú lekciu navrhnutú skupinou
   */
  async rejectGroupLesson(
    lessonId: number,
    reason?: string,
  ): Promise<{ success: boolean; error?: string }> {
    try {
      // Získať lekciu s informáciou o skupine
      const { data: lesson, error: lessonError } = await supabase
        .from("group_lessons")
        .select(
          "*, dance_groups!group_lessons_group_id_fkey(id, name, creator_id)",
        )
        .eq("id", lessonId)
        .single();

      if (lessonError) throw lessonError;
      if (!lesson) throw new Error("Lesson not found");

      // Aktualizovať status na "cancelled"
      const { error: updateError } = await supabase
        .from("group_lessons")
        .update({
          status: "cancelled",
          notes: reason
            ? `${lesson.notes || ""}\n\nZamietnuté: ${reason}`
            : lesson.notes,
          updated_at: new Date().toISOString(),
        })
        .eq("id", lessonId);

      if (updateError) throw updateError;

      // Vytvoriť notifikácie pre všetkých členov skupiny o zamietnutí
      if (lesson.group_id) {
        const { data: members } = await supabase
          .from("group_members")
          .select("user_id")
          .eq("group_id", lesson.group_id)
          .eq("status", "accepted");

        const userIds = members?.map((m) => m.user_id) || [];
        const group = lesson.dance_groups as {
          id: number;
          name: string;
          creator_id: string;
        } | null;

        if (group && !userIds.includes(group.creator_id)) {
          userIds.push(group.creator_id);
        }

        for (const userId of userIds) {
          await notificationsApi.create({
            user_id: userId,
            type: "lesson_cancelled",
            title: `Skupinová lekcia zamietnutá: ${lesson.title}`,
            message: reason
              ? `Vaša skupinová lekcia "${lesson.title}" bola zamietnutá. Dôvod: ${reason}`
              : `Vaša skupinová lekcia "${lesson.title}" bola zamietnutá.`,
            data: {
              lesson_id: lessonId,
              lesson_title: lesson.title,
              lesson_date: lesson.start_at,
              group_id: lesson.group_id,
              group_name: group?.name || "",
            },
            sendEmail: true,
          });
        }
      }

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : "Failed to reject group lesson",
      };
    }
  },

  /**
   * Update a group lesson (for admin)
   */
  async updateGroupLesson(
    lessonId: number,
    updates: Partial<GroupLesson>,
  ): Promise<{ success: boolean; data?: GroupLesson; error?: string }> {
    try {
      const { data, error } = await supabase
        .from("group_lessons")
        .update(updates)
        .eq("id", lessonId)
        .select()
        .single();

      if (error) throw error;

      return { success: true, data: data as GroupLesson };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : "Failed to update group lesson",
      };
    }
  },

  /**
   * Delete a group lesson (for admin)
   */
  async deleteGroupLesson(lessonId: number): Promise<{
    success: boolean;
    error?: string;
  }> {
    try {
      const { error } = await supabase
        .from("group_lessons")
        .delete()
        .eq("id", lessonId);

      if (error) throw error;

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : "Failed to delete group lesson",
      };
    }
  },
};
